/**
 * Les gestes de l'écran de rapprochement manuel.
 *
 * Trois actions, et une seule règle : chacune est **explicite et réversible**.
 * Rien ici ne devine — l'écran a proposé, l'humain a choisi, ces fonctions
 * écrivent ce qu'il a choisi.
 *
 * Les invariants de la base font le reste. Si une répartition dépasse le
 * montant du paiement ou celui de la facture, l'écriture est refusée par
 * PostgreSQL et l'erreur remonte jusqu'à l'écran : elle n'est jamais avalée.
 *
 * ## Une seule porte pour répartir un paiement
 *
 * `allocatePayment` écrit TOUTES les lignes d'une répartition en une insertion.
 * Il y avait auparavant `allocateToInvoices` et `allocateToProvision`, appelées
 * séparément par l'écran — et c'est cette séparation qui a fait disparaître
 * 25,00 € le 4 septembre 2026 : chaque bouton écrivait sa moitié et ignorait
 * l'autre. Les deux sont supprimées ; les laisser aurait invité à les rappeler.
 */

import { db } from './db';
import type { LigneARepartir } from './repartition';
import { lireLesFacturesStructurees, lireLesJumeaux } from './jumeaux.supabase';
import type { FactureDeLaCommunication, JumeauEnFile } from './queueReason';
import {
  COLONNES_DE_TRI, COLONNES_DE_TRI_JOURNAL, lireRecherche,
  type CriteresDuJournal, type LigneDuJournal, type PageDuJournal, type Recherche,
  type Sens, type Tri,
} from './journal';

/** Une ligne de la file : un paiement que l'automatique n'a pas placé. */
export interface PendingPayment {
  id: string;
  valueDate: string;
  amountCents: number;
  unallocatedCents: number;
  communicationRaw: string | null;
  communicationKind: 'structured' | 'legacy' | 'unstructured' | null;
  legacyInvoiceRef: string | null;
  structuredDigits: string | null;
  possiblyTruncated: boolean;
  counterpartyIban: string | null;
  counterpartyName: string | null;
  /** `bank` (relevé CODA), `bank_csv` (export CSV), `stripe`… */
  source: string;
  /**
   * La facture qui porte sa communication structurée : `null` = cherchée, et
   * aucune ; absente = la lecture a échoué. Voir `queueReason`.
   */
  factureStructuree?: FactureDeLaCommunication | null;
  /** La ligne de l'autre source qui décrit le même mouvement, s'il y en a une. */
  jumeau?: JumeauEnFile | null;
}

/**
 * Combien de paiements attendent, et pour quel montant.
 *
 * ## Le défaut que cette fonction corrige
 *
 * La page comptait en **chargeant la file entière** pour en prendre la
 * longueur. PostgREST plafonne une réponse à mille lignes : le badge de
 * l'onglet annonçait donc `1000` — pas mille lignes, mais son propre plafond.
 * Mesuré en production le 6 septembre 2026 : la file compte **8 715 lignes**
 * pour 245 058,18 €.
 *
 * Un compteur qui plafonne sans le dire est pire qu'un compteur absent : il se
 * lit comme une mesure. `count: 'exact'` avec `head: true` demande le nombre
 * sans rapatrier une seule ligne.
 *
 * Le montant, lui, ne peut pas s'agréger par PostgREST. On le laisse à `null`
 * plutôt que d'additionner la page reçue et de faire passer un total partiel
 * pour le total.
 */
export async function countPendingPayments(
  recherche?: string | null,
): Promise<{ nombre: number }> {
  let q = db
    .from('v_payment_allocation_state')
    .select('payment_id', { count: 'exact', head: true })
    .gt('unallocated_cents', 0);
  q = appliquerRecherche(q, lireRecherche(recherche));

  const { count, error } = await q;
  if (error) throw error;
  return { nombre: count ?? 0 };
}

/**
 * Le filtre de recherche de la file, appliqué EN BASE.
 *
 * ## Pourquoi la file a une recherche
 *
 * Signalé le 19 septembre 2026 : « Je cherche un versement de 110 euros pour ce
 * parent mais il n'apparaît pas alors qu'il est bien dans mes extraits. Je
 * pense que Supabase limite le nombre de lignes. »
 *
 * Aucun plafond n'avait joué : le versement attendait dans la file, et
 * l'onglet « Rapprochés » ne montre que ce qui a été DÉCIDÉ. Mais la file,
 * elle, n'avait **aucune recherche** — 3 028 lignes, cent par page. Chercher
 * là où il y avait un champ de recherche était le seul geste possible, et il
 * menait à une conclusion fausse.
 *
 * Les trois formes sont celles du journal, `lireRecherche` étant partagée :
 * une saisie doit se comporter pareil dans les deux écrans, sinon l'un des
 * deux ment sur ce qu'il a compris.
 *
 * | forme   | où l'on cherche |
 * |---------|-----------------|
 * | date    | `value_date`, à l'égalité |
 * | IBAN    | `counterparty_iban`, par préfixe |
 * | montant | `amount_cents`, à l'égalité — plus le texte dès trois caractères |
 * | texte   | `recherche`, par contenance |
 *
 * `recherche` est la colonne ajoutée par la migration `20260919004045` : elle
 * concatène communication, **libellé complet du relevé**, contrepartie et
 * références. Le libellé du relevé n'est pas un détail — le champ
 * communication d'un CODA s'arrête à 53 caractères, et le nom de l'enfant est
 * souvent au-delà.
 */
/* Le type de retour de PostgREST n'est pas exporté par le client ; `any` est
   contenu à cette seule signature plutôt que répandu dans les appelants. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function appliquerRecherche(q: any, recherche: Recherche): any {
  if (recherche.forme === 'date') return q.eq('value_date', recherche.valeur);
  if (recherche.forme === 'iban') return q.ilike('counterparty_iban', `${recherche.valeur}%`);
  if (recherche.forme === 'montant' && recherche.centimes !== undefined) {
    /*
      Dans la file, le montant cherché est celui du VIREMENT : le reliquat
      varie au fil des affectations, et personne ne retient un reliquat.
    */
    const clauses = [`amount_cents.eq.${recherche.centimes}`];
    const propre = recherche.valeur.replace(/[,()%*\\]/g, ' ').trim();
    if (propre.length >= 3) clauses.push(`recherche.ilike.*${propre}*`);
    return q.or(clauses.join(','));
  }
  if (recherche.forme === 'texte') {
    /*
      Les caractères que PostgREST lit comme de la syntaxe — la virgule sépare
      les filtres, la parenthèse groupe — sont retirés plutôt qu'échappés :
      aucun d'eux n'aide à retrouver une communication, et un `%` saisi
      deviendrait un joker silencieux.
    */
    const propre = recherche.valeur.replace(/[,()%*\\]/g, ' ').trim();
    if (propre) return q.ilike('recherche', `%${propre}%`);
  }
  return q;
}

/**
 * La file de rapprochement.
 *
 * ## Le tri est demandé, pas décidé ici
 *
 * L'ordre était figé du plus gros montant au plus petit — un bon choix pour
 * placer l'argent vite, mais pas le seul. Demandé le 6 septembre 2026 :
 * « toujours possibilité de trier du plus ancien au plus récent (tri de base)
 * ou du plus gros montant au plus petit ».
 *
 * Le tri par défaut devient donc **le plus ancien d'abord** : on traite dans
 * l'ordre d'arrivée, ce qui évite qu'un petit paiement de juin reste au fond
 * de la file pour toujours. Le tri par montant reste à un clic.
 *
 * Le tri se fait **en base**, jamais sur la page reçue : trier cent lignes
 * prélevées sur 8 715 ne trie rien, cela réordonne un échantillon.
 */
export async function loadPendingPayments(
  limit = 100,
  tri: Tri = 'date_ancien',
  recherche?: string | null,
): Promise<PendingPayment[]> {
  const ordre = COLONNES_DE_TRI[tri];
  /*
    `unallocated_cents` et non `montant_cents` : dans la file, le montant qui
    compte est ce qui RESTE à placer, pas le mouvement d'origine. Un virement
    de 3 000 € dont il reste 12 € n'est pas un gros dossier.
  */
  const colonne = ordre.colonne === 'montant_cents' ? 'unallocated_cents' : 'value_date';

  let q = db
    .from('v_payment_allocation_state')
    .select(`
      payment_id, source, value_date, amount_cents, unallocated_cents,
      communication_raw, communication_kind, legacy_invoice_ref, structured_digits,
      possibly_truncated, counterparty_iban, counterparty_name
    `)
    .gt('unallocated_cents', 0);
  q = appliquerRecherche(q, lireRecherche(recherche));

  const { data, error } = await q
    .order(colonne, { ascending: ordre.ascendant })
    /* Un second critère, pour que deux lignes du même jour ne s'échangent pas
       d'un chargement à l'autre : une liste qui bouge sans raison fait
       recommencer la lecture. */
    .order('payment_id', { ascending: true })
    .limit(limit);
  if (error) throw error;

  const file: PendingPayment[] = (data ?? []).map((l: Record<string, unknown>) => ({
    id: l.payment_id as string,
    source: String(l.source ?? ''),
    valueDate: l.value_date as string,
    amountCents: l.amount_cents as number,
    unallocatedCents: l.unallocated_cents as number,
    communicationRaw: (l.communication_raw as string) ?? null,
    communicationKind: (l.communication_kind as PendingPayment['communicationKind']) ?? null,
    legacyInvoiceRef: (l.legacy_invoice_ref as string) ?? null,
    structuredDigits: (l.structured_digits as string) ?? null,
    possiblyTruncated: Boolean(l.possibly_truncated),
    counterpartyIban: (l.counterparty_iban as string) ?? null,
    counterpartyName: (l.counterparty_name as string) ?? null,
  }));

  /*
    Ce que la raison de la file ne peut pas deviner : la facture que désigne
    une communication structurée, et le jumeau d'un virement lu deux fois.
    Signalé le 27/09/2026 — « communication structurée inconnue de nos
    factures » pour un virement qui avait soldé la sienne. Deux lectures de
    plus par page ; si l'une échoue, la file s'affiche quand même, et la raison
    le dit au lieu d'affirmer (`factureStructuree` reste absente).
  */
  const [factures, jumeaux] = await Promise.allSettled([
    lireLesFacturesStructurees(file.map((p) => p.structuredDigits ?? '').filter(Boolean)),
    lireLesJumeaux('entree', file.map((p) => ({ id: p.id, valueDate: p.valueDate, amountCents: p.amountCents }))),
  ]);
  for (const p of file) {
    if (p.structuredDigits && factures.status === 'fulfilled') {
      p.factureStructuree = factures.value.get(p.structuredDigits) ?? null;
    }
    if (jumeaux.status === 'fulfilled') p.jumeau = jumeaux.value.get(p.id) ?? null;
  }
  return file;
}

/**
 * Écrit une répartition entière, en **une seule** insertion.
 *
 * ## Pourquoi une seule
 *
 * Signalé le 4 septembre 2026 : une mère paie 60,00 €, il reste une facture de
 * 35,00 € à son nom. « Les 35 euros sont bien affectés mais les 25 euros qui
 * restent disparaissent. » L'écran avait deux boutons, chacun écrivant sa
 * moitié et ignorant l'autre — et « Reste en provision » jetait les montants
 * saisis dans les cases des factures.
 *
 * Une insertion multi-lignes est atomique : ou tout est écrit, ou rien ne
 * l'est. Il n'existe plus d'état intermédiaire où l'argent est à moitié placé
 * et où le reliquat n'a plus de chemin vers la provision.
 *
 * Les invariants restent ceux de la base : le déclencheur `payment_allocations_sync`
 * vérifie ligne par ligne que le paiement n'est pas sur-affecté et qu'aucune
 * facture ne reçoit plus qu'elle ne réclame. Comme la somme ne fait que
 * croître, tout préfixe d'une répartition valable est valable : l'ordre des
 * lignes est donc indifférent.
 *
 * La méthode enregistrée est `manual` : on garde la trace que c'est un humain
 * qui a décidé, ce qui permettra plus tard de mesurer ce que l'automatique
 * rate vraiment.
 */
export async function allocatePayment(
  paymentId: string,
  lignes: LigneARepartir[],
): Promise<void> {
  const aEcrire = lignes.filter((l) => l.amountCents > 0);
  if (aEcrire.length === 0) return;

  const { error } = await db.from('payment_allocations').insert(
    aEcrire.map((l) => ({
      payment_id: paymentId,
      amount_cents: l.amountCents,
      target: l.target,
      /*
        Les colonnes non concernées sont explicitement nulles. Le CHECK
        `payment_allocations_target_coherent` exige `invoice_id` nul pour une
        provision et les deux nuls pour un hors périmètre : les laisser
        absentes marcherait, les écrire nulles le dit.
      */
      invoice_id: l.invoiceId ?? null,
      provision_user_id: l.provisionUserId ?? null,
      method: 'manual' as const,
      notes: l.note ?? null,
    })),
  );
  if (error) throw error;
}

/**
 * Classe un paiement hors du périmètre des centres de vacances.
 *
 * Le montant entier y passe : un paiement de garderie ou de facturation
 * partenaire n'est pas à moitié hors périmètre. La catégorie devinée par la
 * forme est conservée dans les notes, pour qu'on sache plus tard **pourquoi**
 * ce paiement a été écarté.
 */
export async function markOutOfScope(
  paymentId: string,
  amountCents: number,
  note: string,
): Promise<void> {
  const { error } = await db.from('payment_allocations').insert({
    payment_id: paymentId,
    amount_cents: amountCents,
    target: 'out_of_scope' as const,
    method: 'manual' as const,
    notes: note,
  });
  if (error) throw error;
}

/**
 * Classe plusieurs paiements hors périmètre d'un seul geste.
 *
 * Constaté à l'usage : sur le premier lot réel, 25 des 26 paiements de la file
 * étaient des communications structurées d'un autre système, et il a fallu
 * 25 clics identiques. Ce n'est pas un confort — un écran qui impose 25 gestes
 * identiques finit par être utilisé sans regarder, et c'est là que les erreurs
 * se produisent.
 *
 * Une seule écriture, donc un seul refus possible : si une ligne viole un
 * invariant, rien n'est écrit et l'erreur est montrée.
 */
export async function markManyOutOfScope(
  lignes: { paymentId: string; amountCents: number; note: string }[],
): Promise<number> {
  const aEcrire = lignes.filter((l) => l.amountCents > 0);
  if (aEcrire.length === 0) return 0;

  const { error } = await db.from('payment_allocations').insert(
    aEcrire.map((l) => ({
      payment_id: l.paymentId,
      amount_cents: l.amountCents,
      target: 'out_of_scope' as const,
      method: 'manual' as const,
      notes: l.note,
    })),
  );
  if (error) throw error;
  return aEcrire.length;
}

/**
 * Rattache un IBAN à une famille, de façon confirmée.
 *
 * C'est le geste qui fait gagner du temps la fois suivante : le prochain
 * paiement de ce compte remonte en tête des suggestions avec le poids le plus
 * fort. `confirmed_by` distingue cette association d'une simple observation —
 * seules les confirmées servent de piste forte.
 */
export async function linkIbanToFamily(
  iban: string,
  userId: string,
  displayName: string | null,
): Promise<void> {
  const { data: session } = await db.auth.getUser();

  const { error } = await db.from('payment_counterparties').upsert(
    {
      iban,
      user_id: userId,
      display_name: displayName,
      confirmed_by: session?.user?.id ?? null,
      confirmed_at: new Date().toISOString(),
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: 'iban' },
  );
  if (error) throw error;
}

/**
 * Annule une allocation.
 *
 * Indispensable, et pas un ornement : un rapprochement manuel se trompe, et un
 * outil dont on ne peut pas défaire les gestes n'est pas utilisable sur de
 * l'argent réel. La suppression fait recalculer `allocated_cents` par le
 * déclencheur, donc le paiement retourne dans la file.
 */
export async function undoAllocation(allocationId: string): Promise<void> {
  const { error } = await db.from('payment_allocations').delete().eq('id', allocationId);
  if (error) throw error;
}

/** Les allocations d'un paiement, pour pouvoir les défaire. */
export async function loadAllocations(paymentId: string): Promise<
  {
    id: string; amountCents: number; target: string;
    invoiceNumber: string | null; invoicePdfUrl: string | null; notes: string | null;
  }[]
> {
  const { data, error } = await db
    .from('payment_allocations')
    .select('id, amount_cents, target, notes, invoices(invoice_number, pdf_url)')
    .eq('payment_id', paymentId)
    .order('created_at');
  if (error) throw error;

  return (data ?? []).map((a: Record<string, unknown>) => ({
    id: a.id as string,
    amountCents: a.amount_cents as number,
    target: a.target as string,
    invoiceNumber: (a.invoices as { invoice_number: string } | null)?.invoice_number ?? null,
    invoicePdfUrl: (a.invoices as { pdf_url: string | null } | null)?.pdf_url ?? null,
    notes: (a.notes as string) ?? null,
  }));
}

/*
  `loadRecentAllocations` et `AllocationEntry` ont été SUPPRIMÉES le 6 septembre
  2026, remplacées par `loadJournal`.

  Elles lisaient les cent dernières affectations, et l'écran filtrait ensuite
  dans le navigateur. Le journal porte 4 152 entrées : une recherche par IBAN
  n'aurait donc interrogé que les cent dernières lignes, et rendu « rien
  trouvé » sur un compte présent quatre mille lignes plus loin.

  Elles ne sont pas conservées « au cas où » : les laisser aurait invité à les
  rappeler, et c'est exactement ce qui est arrivé à `allocateToInvoices` et
  `allocateToProvision` avant leur suppression.
*/


/**
 * La communication d'un paiement, en entier.
 *
 * Le champ communication d'un CODA fait 53 caractères et **tronque au-delà**.
 * Cas réel du 24 août 2026 :
 *
 *     CDV-NDSC-2601965/Monica William 95 euros-92 euros not
 *
 * La phrase de la mère s'arrête au milieu d'un mot, sur un paiement de 3,00 €
 * pour une facture de 95,00 €. Ce qu'elle explique est exactement ce qu'il
 * faut savoir — et le libellé complet du relevé, lui, le porte.
 *
 * Lu à la demande, quand une ligne est dépliée : ce texte fait deux à trois
 * cents caractères par paiement et n'a pas à voyager pour toute une file.
 *
 * `free_text` est ajoutée par la migration 20260903111107. Si elle n'est pas
 * encore appliquée, la lecture se rabat sur la communication seule plutôt que
 * d'échouer : mieux vaut 53 caractères que rien.
 */
export interface TextePaiement {
  communicationRaw: string | null;
  freeText: string | null;
  /** Vrai quand la colonne n'existe pas encore : l'écran peut le dire. */
  freeTextUnavailable: boolean;
}

export async function loadPaymentText(paymentId: string): Promise<TextePaiement> {
  const { data, error } = await db
    .from('payments')
    .select('communication_raw, free_text')
    .eq('id', paymentId)
    .maybeSingle();

  if (!error) {
    const l = (data ?? {}) as { communication_raw?: string | null; free_text?: string | null };
    return {
      communicationRaw: l.communication_raw ?? null,
      freeText: l.free_text ?? null,
      freeTextUnavailable: false,
    };
  }

  const message = (error as { message?: string }).message ?? '';
  if (!message.includes('free_text')) throw error;

  const repli = await db
    .from('payments')
    .select('communication_raw')
    .eq('id', paymentId)
    .maybeSingle();
  if (repli.error) throw repli.error;
  return {
    communicationRaw: (repli.data as { communication_raw?: string | null } | null)?.communication_raw ?? null,
    freeText: null,
    freeTextUnavailable: true,
  };
}

/**
 * Le journal de rapprochement : les deux sens, filtrés et cherchés **en base**.
 *
 * ## Pourquoi cette fonction remplace `loadRecentAllocations`
 *
 * Celle-ci lisait les cent dernières affectations, et l'écran filtrait ensuite
 * dans le navigateur. Mesuré en production le 6 septembre 2026 : le journal
 * porte **4 152 entrées** et **45 dépenses**. Chercher un IBAN côté navigateur
 * n'aurait donc interrogé que les cent dernières lignes, et rendu « rien
 * trouvé » sur un compte présent quatre mille lignes plus loin.
 *
 * **Un écran qui dit « rien trouvé » alors que la ligne existe est pire qu'un
 * écran sans recherche** : on en tire une conclusion, et ici la conclusion
 * serait qu'un remboursement n'a pas été fait.
 *
 * Tout passe donc par `v_reconciliation_journal`, qui unit les deux sens et
 * concatène dans sa colonne `recherche` tout ce qui identifie une ligne.
 *
 * ## Ce que la forme de la saisie change
 *
 * | forme  | où l'on cherche |
 * |--------|-----------------|
 * | date   | `date_valeur`, à l'égalité |
 * | IBAN   | `compte_contrepartie`, par préfixe |
 * | texte  | `recherche`, par contenance |
 *
 * La règle qui décide de la forme vit dans `journal.ts`, sans réseau, et elle
 * est éprouvée : c'est un test qui a trouvé qu'elle prenait « Dupont » pour un
 * IBAN.
 */
export async function loadJournal(criteres: CriteresDuJournal): Promise<PageDuJournal> {
  /*
    La liste du JOURNAL, qui porte un tri de plus que celle de la file :
    l'ordre des décisions. `COLONNES_DE_TRI` n'en connaît pas la colonne et
    aurait rendu `undefined` — un tri silencieusement inopérant.
  */
  const ordre = COLONNES_DE_TRI_JOURNAL[criteres.tri];
  const recherche = lireRecherche(criteres.recherche);

  let q = db
    .from('v_reconciliation_journal')
    .select(
      `sens, ligne_id, decide_le, date_valeur, montant_cents, mouvement_cents,
       cible, methode, notes, payment_id, communication,
       compte_contrepartie, nom_contrepartie,
       piece_numero, piece_type, piece_pdf, famille`,
      /* Le total réel, pour que l'écran puisse dire ce qu'il ne montre pas. */
      { count: 'exact' },
    );

  if (criteres.sens) q = q.eq('sens', criteres.sens);

  if (recherche.forme === 'date') {
    q = q.eq('date_valeur', recherche.valeur);
  } else if (recherche.forme === 'iban') {
    /*
      Par préfixe : on cherche un compte, et un IBAN se lit du début. Le `%`
      final suffit — chercher au milieu d'un IBAN n'a pas de sens, et un
      fragment sans code pays est de toute façon parti en recherche textuelle.
    */
    q = q.ilike('compte_contrepartie', `${recherche.valeur}%`);
  } else if (recherche.forme === 'montant' && recherche.centimes !== undefined) {
    /*
      Un montant se cherche des DEUX côtés : `montant_cents` est ce que la
      ligne a placé, `mouvement_cents` ce que le virement portait. Chercher
      2 603,28 EUR doit retrouver le virement même quand il a été coupé en
      trois lignes — sans quoi le geste qu'on veut défaire reste introuvable.

      Et le texte s'y ajoute dès trois caractères : « 2603 » peut être 26,03 EUR
      comme un fragment de numéro, et les deux lectures sont légitimes.
    */
    const clauses = [
      `montant_cents.eq.${recherche.centimes}`,
      `mouvement_cents.eq.${recherche.centimes}`,
    ];
    const propre = recherche.valeur.replace(/[,()%*\\]/g, ' ').trim();
    if (propre.length >= 3) clauses.push(`recherche.ilike.*${propre}*`);
    q = q.or(clauses.join(','));
  } else if (recherche.forme === 'texte') {
    /*
      Les caractères que PostgREST lit comme de la syntaxe — la virgule sépare
      les filtres, la parenthèse groupe — sont retirés plutôt qu'échappés :
      aucun d'eux n'aide à retrouver une communication, et un `%` saisi
      deviendrait un joker silencieux.
    */
    const propre = recherche.valeur.replace(/[,()%*\\]/g, ' ').trim();
    if (propre) q = q.ilike('recherche', `%${propre}%`);
  }

  const { data, error, count } = await q
    .order(ordre.colonne, { ascending: ordre.ascendant })
    /* Départage stable : sans lui, deux lignes du même jour s'échangent d'une
       page à l'autre et une ligne peut être vue deux fois ou jamais. */
    .order('ligne_id', { ascending: true })
    .range(criteres.depuis, criteres.depuis + criteres.taille - 1);
  if (error) throw error;

  const lignes: LigneDuJournal[] = ((data ?? []) as Record<string, unknown>[]).map((l) => ({
    sens: l.sens as Sens,
    ligneId: l.ligne_id as string,
    decideLe: l.decide_le as string,
    dateValeur: l.date_valeur as string,
    montantCents: Number(l.montant_cents),
    mouvementCents: Number(l.mouvement_cents),
    cible: String(l.cible),
    methode: String(l.methode),
    notes: (l.notes as string) ?? null,
    paymentId: (l.payment_id as string) ?? null,
    communication: (l.communication as string) ?? null,
    compteContrepartie: (l.compte_contrepartie as string) ?? null,
    nomContrepartie: (l.nom_contrepartie as string) ?? null,
    pieceNumero: (l.piece_numero as string) ?? null,
    pieceType: (l.piece_type as LigneDuJournal['pieceType']) ?? null,
    piecePdf: (l.piece_pdf as string) ?? null,
    famille: (l.famille as string) ?? null,
  }));

  return {
    lignes,
    total: count ?? null,
    centimesAffiches: lignes.reduce((s, l) => s + l.montantCents, 0),
  };
}
